/**
 * Tiny qurvo facade that queues calls until the real SDK loads. Lets
 * the SDK chunk stay out of the eager bundle — callers import this
 * instead of `@qurvo/sdk-browser` directly. When initAnalytics() runs
 * (lazy, idle-callback-driven in landing-app) the queue is flushed
 * onto the loaded qurvo instance.
 *
 * Datadog RUM auto-instruments the initial page view via reactPlugin,
 * but the TMA shell never changes URL — every reducer screen collapses
 * into one `/` view. `datadogView.start(name)` names each screen as a
 * distinct RUM view. It goes through the same buffer so naming a screen
 * that changed before the SDK finished its idle-time load isn't lost,
 * and app code still never imports `@datadog/browser-rum` eagerly.
 */

type QurvoIdentifyArgs = [userId: string, traits?: Record<string, unknown>];
type QurvoTrackArgs = [event: string, props?: Record<string, unknown>];
type QurvoScreenArgs = [name: string, props?: Record<string, unknown>];

type QueuedCall =
  | { kind: 'identify'; args: QurvoIdentifyArgs }
  | { kind: 'track'; args: QurvoTrackArgs }
  | { kind: 'screen'; args: QurvoScreenArgs };

interface RealQurvo {
  identify: (...args: QurvoIdentifyArgs) => void;
  track: (...args: QurvoTrackArgs) => void;
  screen: (...args: QurvoScreenArgs) => void;
}

let real: RealQurvo | null = null;
const queue: QueuedCall[] = [];

export function bindRealQurvo(instance: RealQurvo): void {
  real = instance;
  while (queue.length > 0) {
    const call = queue.shift()!;
    try {
      switch (call.kind) {
        case 'identify':
          instance.identify(...call.args);
          break;
        case 'track':
          instance.track(...call.args);
          break;
        case 'screen':
          instance.screen(...call.args);
          break;
      }
    } catch (err) {
      console.error('[analyticsQueue] flushed call failed:', err);
    }
  }
}

export const qurvo = {
  identify(...args: QurvoIdentifyArgs): void {
    if (real) real.identify(...args);
    else queue.push({ kind: 'identify', args });
  },
  track(...args: QurvoTrackArgs): void {
    if (real) real.track(...args);
    else queue.push({ kind: 'track', args });
  },
  screen(...args: QurvoScreenArgs): void {
    if (real) real.screen(...args);
    else queue.push({ kind: 'screen', args });
  },
};

let boundStartView: ((name: string) => void) | null = null;
// Only the latest screen matters if several changed before the SDK bound —
// intermediate screens the user has already left aren't worth a retroactive view.
let pendingView: string | null = null;

export function bindDatadogView(startView: (name: string) => void): void {
  boundStartView = startView;
  if (pendingView !== null) {
    try {
      startView(pendingView);
    } catch (err) {
      console.error('[analyticsQueue] datadog startView failed:', err);
    }
    pendingView = null;
  }
}

export const datadogView = {
  start(name: string): void {
    if (boundStartView) boundStartView(name);
    else pendingView = name;
  },
};
